import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Printer, FileText, Download, ChevronDown, ChevronRight } from 'lucide-react';
import { printHtmlSilently } from '../lib/silentPrint';
import { downloadCsv } from './DateRangeFilter';
import { CLOSING_METHODS, closingLabel } from '../lib/closingMethods';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');
const todayStr = () => new Date().toLocaleDateString('en-CA');
const label = (m) => (m === 'outros' ? 'Outros' : closingLabel(m));

export const ReceiptsByPeriodReport = () => {
  const [start, setStart] = useState(todayStr());
  const [end, setEnd] = useState(todayStr());
  const [method, setMethod] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({}); // cliente → aberto?
  // Uma linha por cliente (total pago, último pagamento, formas e recebedores); clique expande as notas
  const grouped = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    data.items.forEach((it) => {
      const key = it.customer_id || it.customer_name || 'sem-cliente';
      if (!map.has(key)) map.set(key, { key, customer_name: it.customer_name || 'Cliente não identificado', items: [], total: 0, last_at: null, methods: new Set(), received_by: new Set() });
      const g = map.get(key);
      g.items.push(it);
      g.total += Number(it.amount) || 0;
      if (!g.last_at || new Date(it.received_at) > new Date(g.last_at)) g.last_at = it.received_at;
      if (it.payment_method) g.methods.add(it.payment_method);
      if (it.received_by) g.received_by.add(it.received_by);
    });
    return [...map.values()]
      .map((g) => ({ ...g, methods: [...g.methods], received_by: [...g.received_by], items: g.items.slice().sort((a, b) => new Date(b.received_at) - new Date(a.received_at)) }))
      .sort((a, b) => new Date(b.last_at) - new Date(a.last_at));
  }, [data]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ start_date: start, end_date: end });
      if (method) params.set('payment_method', method);
      const { data: d } = await axios.get(`${API}/accounts/receivables/receipts/report?${params}`, { withCredentials: true });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao gerar relatório');
    } finally { setLoading(false); }
  };

  const handlePrint = () => {
    if (!data) return;
    const rows = data.items.map((it) => `
      <tr><td>${fmtDateTime(it.received_at)}</td><td class="right">${money(it.amount)}</td></tr>
      <tr><td colspan="2">${it.customer_name}${it.sale_number ? ` #${it.sale_number}` : ''} - ${label(it.payment_method)}</td></tr>`).join('');
    const byMethod = data.by_method.map((m) => `<tr><td>${label(m.method)}</td><td class="right">${money(m.total)}</td></tr>`).join('');
    printHtmlSilently(`<html><head><style>
      body{font-family:'Courier New',monospace;font-size:11px;color:#000}
      table{width:100%;border-collapse:collapse} td{padding:1px 0;vertical-align:top} .right{text-align:right} .center{text-align:center} .bold{font-weight:bold}
      hr{border:none;border-top:1px dashed #000;margin:4px 0}</style></head><body>
      <div class="center bold">RECEBIMENTOS DO CREDIÁRIO</div>
      <div class="center">${fmtDate(start + 'T12:00:00')} a ${fmtDate(end + 'T12:00:00')}</div><hr/>
      <table>${rows}</table><hr/>
      <div class="bold">Por forma de pagamento</div>
      <table>${byMethod}</table><hr/>
      <table>
        <tr><td>Recebimentos</td><td class="right">${data.count}</td></tr>
        <tr><td class="bold">TOTAL RECEBIDO</td><td class="right bold">${money(data.total)}</td></tr>
      </table></body></html>`);
  };

  const handleCsv = () => data && downloadCsv(`recebimentos_${start}_a_${end}.csv`, data.items, [
    { key: 'received_at', label: 'Data/Hora', format: fmtDateTime },
    { key: 'customer_name', label: 'Cliente' },
    { key: 'sale_number', label: 'Venda' },
    { key: 'payment_method', label: 'Forma', format: label },
    { key: 'amount', label: 'Valor (R$)', format: (v) => Number(v || 0).toFixed(2).replace('.', ',') },
    { key: 'received_by', label: 'Recebido por' },
  ]);

  return (
    <div className="space-y-4" data-testid="receipts-report">
      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end overflow-hidden">
        <div><Label className="text-xs">Data inicial</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="receipts-report-start" /></div>
        <div><Label className="text-xs">Data final</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="receipts-report-end" /></div>
        <div>
          <Label className="text-xs">Forma de pagamento</Label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="receipts-report-method">
            <option value="">Todas</option>
            {CLOSING_METHODS.filter((m) => m.key !== 'cartao').map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>
        <Button onClick={load} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 h-10" data-testid="receipts-report-generate">
          <FileText size={16} className="mr-2" /> {loading ? 'Gerando...' : 'Gerar Relatório'}
        </Button>
        {data && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handlePrint} className="h-10" data-testid="receipts-report-print"><Printer size={16} className="mr-1" /> Imprimir</Button>
            <Button variant="outline" onClick={handleCsv} className="h-10" data-testid="receipts-report-csv"><Download size={16} className="mr-1" /> CSV</Button>
          </div>
        )}
      </div>

      {error && <p className="text-red-600 text-sm" data-testid="receipts-report-error">{error}</p>}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-emerald-600 text-white rounded-md p-4" data-testid="receipts-report-total">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-100">Total recebido</p>
              <p className="text-3xl font-black mt-1">{money(data.total)}</p>
              <p className="text-xs text-emerald-100 mt-1">{data.count} recebimento(s)</p>
            </div>
            <div className="md:col-span-2 bg-white border border-slate-200 rounded-md p-4" data-testid="receipts-report-by-method">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Por forma de pagamento</p>
              {data.by_method.length === 0 && <p className="text-sm text-slate-400">Sem recebimentos no período.</p>}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {data.by_method.map((m) => (
                  <div key={m.method} className="flex justify-between items-center bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm" data-testid={`receipts-method-${m.method}`}>
                    <span className="text-slate-600">{label(m.method)}</span><b>{money(m.total)}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm" data-testid="receipts-report-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <tr><th className="p-3 text-left">Último pagamento</th><th className="p-3 text-left">Cliente</th><th className="p-3 text-left">Forma</th><th className="p-3 text-right">Total pago</th><th className="p-3 text-left">Recebido por</th></tr>
              </thead>
              <tbody>
                {grouped.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400" data-testid="receipts-report-empty">Nenhum recebimento no período.</td></tr>}
                {grouped.map((g) => {
                  const open = !!expanded[g.key];
                  return (
                    <React.Fragment key={g.key}>
                      <tr onClick={() => setExpanded((e) => ({ ...e, [g.key]: !open }))} className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${open ? 'bg-emerald-50/40' : ''}`} data-testid={`receipts-group-${g.key}`}>
                        <td className="p-3 whitespace-nowrap"><span className="inline-flex items-center gap-1">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {fmtDateTime(g.last_at)}</span></td>
                        <td className="p-3 font-bold text-slate-900">{g.customer_name} <span className="ml-1 text-[10px] font-normal text-slate-500">{g.items.length} nota(s)</span></td>
                        <td className="p-3">{g.methods.map(label).join(', ')}</td>
                        <td className="p-3 text-right font-bold text-emerald-700" data-testid={`receipts-group-total-${g.key}`}>{money(g.total)}</td>
                        <td className="p-3 text-slate-500">{g.received_by.join(', ') || '—'}</td>
                      </tr>
                      {open && g.items.map((it, i) => (
                        <tr key={`${it.receivable_id}-${i}`} className="border-t border-dashed border-slate-100 bg-slate-50/60 text-xs" data-testid={`receipts-item-${g.key}-${i}`}>
                          <td className="p-2 pl-10 text-slate-600">{fmtDateTime(it.received_at)}</td>
                          <td className="p-2 text-blue-700 font-bold">{it.sale_number ? `Venda #${it.sale_number}` : 'Nota avulsa'}</td>
                          <td className="p-2">{label(it.payment_method)}</td>
                          <td className="p-2 text-right font-bold text-emerald-700">{money(it.amount)}</td>
                          <td className="p-2 text-slate-500">{it.received_by || '—'}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ReceiptsByPeriodReport;
